import { Link } from 'react-router-dom';
import SectionHeading from '@/components/base/SectionHeading';
import StatCard from '@/components/base/StatCard';
import EquityAndDrawdown from '@/components/feature/EquityAndDrawdown';
import Reveal from '@/components/feature/Reveal';
import ZoomableImage from '@/components/feature/ZoomableImage';
import PerformanceBoundary from '@/components/feature/PerformanceBoundary';

/**
 * Same real MetaTrader 5 terminal balance/equity screenshot used on the
 * Performance page, so the home preview and the detail page tell one story.
 * Its container keeps the matching wide aspect ratio below to avoid cropping.
 */
const EQUITY_SHOT = '/image/mt5-equity.png';

const METRIC_ICONS: Record<string, string> = {
  'total-return': 'ri-percent-line',
  'win-rate': 'ri-trophy-line',
  'profit-factor': 'ri-scales-3-line',
  'max-dd': 'ri-arrow-down-double-line',
};

export default function PerformancePreview() {
  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-shell mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="Live track record"
              title="Real MT5 account performance, not backtest screenshots"
              subtitle="These figures come straight from a live gold account we actually trade. No demo masking, no cherry-picked months — the full account history is published right here on this page."
            />
          </Reveal>

          <PerformanceBoundary label="live performance">
            {(data) => {
              const { headlineMetrics, detailMetrics, accountSnapshot, weeklyEquity, monthlyEquity } = data;
              const profitFactor =
                headlineMetrics.find((metric) => metric.id === 'profit-factor')?.value ?? '';
              const maxDrawdown =
                headlineMetrics.find((metric) => metric.id === 'max-dd')?.value ?? '';

              return (
                <>
                  <div className="mt-10 md:mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
                    {headlineMetrics.map((metric, index) => (
                      <Reveal key={metric.id} delay={index * 80}>
                        <StatCard
                          label={metric.label}
                          value={metric.value}
                          caption={metric.caption}
                          tone={metric.tone}
                          icon={METRIC_ICONS[metric.id]}
                        />
                      </Reveal>
                    ))}
                  </div>

                  <Reveal delay={80}>
                    <div className="mt-6 flex items-start gap-3 rounded-lg border border-secondary-200 bg-secondary-100/60 px-4 py-3.5 md:px-5">
                      <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-md bg-secondary-200 text-secondary-800">
                        <i className="ri-information-line text-base" aria-hidden="true"></i>
                      </span>
                      <p className="text-xs md:text-sm leading-relaxed text-foreground-700">
                        <strong className="font-semibold text-foreground-950">Full disclosure:</strong> the edge
                        here is modest (profit factor {profitFactor}) and the account rode a {maxDrawdown} peak-to-valley drawdown.
                        Gold drawdowns like that are real — we show the record exactly as it happened instead of
                        hiding the ugly months.
                      </p>
                    </div>
                  </Reveal>

                  <div className="mt-6 md:mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Reveal className="lg:col-span-2">
                      <div className="h-full rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950">
                              Account Equity Growth
                            </h3>
                            <p className="mt-1 text-xs md:text-sm text-foreground-500">
                              Account #{accountSnapshot.accountNumber} · {accountSnapshot.broker}
                            </p>
                            <p className="mt-0.5 text-xs text-foreground-500/80">
                              Balance in USC · updated {accountSnapshot.updatedAt}
                            </p>
                          </div>
                          <span className="inline-flex items-center gap-2 text-xs text-foreground-600 whitespace-nowrap">
                            <span className="inline-block w-2.5 h-2.5 rounded-full bg-accent-500" aria-hidden="true"></span>
                            Latest balance
                          </span>
                        </div>
                        <div className="mt-6">
                          <EquityAndDrawdown weekly={weeklyEquity} monthly={monthlyEquity} />
                        </div>

                        {/* Terminal cross-check — the platform's own plot of the same account */}
                        <div className="mt-6 border-t border-background-200 pt-5">
                          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                              <i className="ri-line-chart-line text-sm" aria-hidden="true"></i>
                            </span>
                            <h4 className="font-heading text-sm font-semibold text-foreground-950">
                              Straight from the terminal
                            </h4>
                            <span className="text-xs text-foreground-500">
                              — the same account, as MetaTrader 5 plots it
                            </span>
                          </div>
                          <div className="relative mt-4 aspect-[40/9] w-full overflow-hidden rounded-md border border-background-200 bg-background-100">
                            <ZoomableImage
                              src={EQUITY_SHOT}
                              alt={`MetaTrader 5 terminal balance and equity curve for account ${accountSnapshot.accountNumber}`}
                              title="MT5 · Balance / equity curve"
                              className="absolute inset-0 h-full w-full"
                              imageClassName="h-full w-full object-contain object-top"
                            />
                          </div>
                          <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                            The curve above is rebuilt by this site from the raw trade log; the screenshot is the
                            MetaTrader 5 terminal&apos;s own chart of the same account — a second, independent view
                            you can enlarge. The full report sits on the{' '}
                            <Link to="/performance" className="font-semibold text-primary-600 hover:text-primary-700 transition-colors">
                              performance page
                            </Link>
                            .
                          </p>
                        </div>
                      </div>
                    </Reveal>

                    <div className="lg:col-span-1">
                      {/* Sticky lives OUTSIDE the reveal wrapper — a transformed
                          ancestor would otherwise break position: sticky. */}
                      <div className="lg:sticky lg:top-24">
                        <Reveal delay={120}>
                          <div className="rounded-lg bg-primary-950 p-5 md:p-6 flex flex-col">
                            <div className="flex items-center gap-2">
                              <i className="ri-stack-line text-accent-400 text-lg" aria-hidden="true"></i>
                              <h3 className="font-heading text-base md:text-lg font-semibold text-background-50">
                                Detailed Statistics
                              </h3>
                            </div>
                            <ul className="mt-5 flex flex-col divide-y divide-background-50/10">
                              {detailMetrics.map((metric) => (
                                <li key={metric.id} className="flex items-center justify-between gap-3 py-3">
                                  <span className="text-xs md:text-sm text-background-200/80">{metric.label}</span>
                                  <span className="font-num text-sm md:text-base font-semibold text-background-50 whitespace-nowrap">
                                    {metric.value}
                                  </span>
                                </li>
                              ))}
                            </ul>
                            <Link
                              to="/performance"
                              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-accent-300 hover:text-accent-200 transition-colors"
                            >
                              View full performance report
                              <i className="ri-arrow-right-line" aria-hidden="true"></i>
                            </Link>
                          </div>
                        </Reveal>
                      </div>
                    </div>
                  </div>
                </>
              );
            }}
          </PerformanceBoundary>
        </div>
      </div>
    </section>
  );
}